/**
 * useCatchMeUp — the React binding for Catch Me Up (section 19b).
 *
 * Picks a "since" point (yesterday / last week / last sprint / my leave /
 * custom date), retrieves entities created-or-updated since then CLIENT-SIDE,
 * and calls REAL Bedrock to produce a get-back-up-to-speed briefing. The
 * feeding entities are returned as `usedRefs` for transparency + linking.
 */

import { useCallback, useMemo, useState } from 'react';
import { useContextSnapshot } from './useContextSnapshot';
import { formatContext, refsInRange, sortByRecency } from './contextRetriever';
import {
  SUMMARY_MAX_TOKENS,
  SUMMARY_TEMPERATURE,
  buildCatchMeUpPrompt,
  buildCatchMeUpSystemPrompt,
  formatKbSources,
  friendlyInferenceError,
  sinceForPreset,
} from './intelligenceModel';
import type { CatchMeUpPreset, CatchMeUpResult } from './types';

export interface UseCatchMeUpResult {
  preset: CatchMeUpPreset;
  setPreset: (preset: CatchMeUpPreset) => void;
  /** yyyy-mm-dd used by the `myLeave` / `custom` presets. */
  customDate: string;
  setCustomDate: (date: string) => void;
  /** True when the active preset needs the date input. */
  needsDate: boolean;
  sinceIso: string;
  sinceCount: number;
  result: CatchMeUpResult | null;
  loading: boolean;
  generating: boolean;
  error: string | null;
  /** When true, augments the prompt with KB-retrieved passages. */
  useKb: boolean;
  setUseKb: (useKb: boolean) => void;
  generate: () => Promise<void>;
}

export function useCatchMeUp(): UseCatchMeUpResult {
  const { refs, loading, error, converse, kbRetrieve } = useContextSnapshot();
  const [preset, setPreset] = useState<CatchMeUpPreset>('lastWeek');
  const [customDate, setCustomDate] = useState('');
  const [result, setResult] = useState<CatchMeUpResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [useKb, setUseKb] = useState(false);

  const needsDate = preset === 'myLeave' || preset === 'custom';
  const sinceIso = useMemo(
    () => sinceForPreset(preset, customDate),
    [preset, customDate],
  );

  const since = useMemo(
    () => sortByRecency(refsInRange(refs, sinceIso, new Date().toISOString())),
    [refs, sinceIso],
  );

  const generate = useCallback(async () => {
    setGenerating(true);
    const usedRefs = since;
    const context = formatContext(usedRefs);
    let kbContext = '';
    if (useKb) {
      try {
        const sources = await kbRetrieve(
          'Recent changes, decisions, blockers, and documents to catch up on',
          8,
        );
        kbContext = formatKbSources(sources);
      } catch {
        /* KB unavailable — proceed with entity-only context */
      }
    }
    try {
      const markdown = await converse(
        buildCatchMeUpPrompt(context, sinceIso, kbContext),
        buildCatchMeUpSystemPrompt(),
        { maxTokens: SUMMARY_MAX_TOKENS, temperature: SUMMARY_TEMPERATURE },
      );
      setResult({
        preset,
        sinceIso,
        markdown: markdown.trim() || '_(empty response)_',
        usedRefs,
      });
    } catch (e) {
      const raw = e instanceof Error ? e.message : 'Inference request failed.';
      setResult({ preset, sinceIso, markdown: friendlyInferenceError(raw), usedRefs });
    } finally {
      setGenerating(false);
    }
  }, [since, sinceIso, preset, converse, useKb, kbRetrieve]);

  return {
    preset,
    setPreset,
    customDate,
    setCustomDate,
    needsDate,
    sinceIso,
    sinceCount: since.length,
    result,
    loading,
    generating,
    error,
    useKb,
    setUseKb,
    generate,
  };
}
